import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail, API_ORIGIN } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, Film, Image as ImageIcon, Loader2, MessageSquare, Mic, Pencil, Plus, RotateCcw, Search, Sticker, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Saudações", "Diálogos Formais", "Gírias & Informais", "Dúvidas Frequentes", "Áudios & Mídias", "Enquetes"];
const FILES_BASE = `${API_ORIGIN}/api/files`;
const TIPO_LABEL = { texto: "Texto", imagem: "Imagem", video: "Vídeo", figurinha: "Figurinha", audio: "Áudio", link: "Link", localizacao: "Localização", enquete: "Enquete", enquete_voto: "Voto" };
const DIAS_SEMANA = [["Seg", 0], ["Ter", 1], ["Qua", 2], ["Qui", 3], ["Sex", 4], ["Sáb", 5], ["Dom", 6]];
const MEDIA_TIPOS = ["imagem", "video", "figurinha", "audio"];
const mediaSrc = (p) => (p && p.startsWith("http") ? p : `${FILES_BASE}/${p}`);

export default function Frases() {
  const [frases, setFrases] = useState([]);
  const [search, setSearch] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [novoTexto, setNovoTexto] = useState("");
  const [novaCategoria, setNovaCategoria] = useState(CATEGORIES[0]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTexto, setEditTexto] = useState("");
  const [loading, setLoading] = useState(true);
  const [mediaCategoria, setMediaCategoria] = useState("Áudios & Mídias");
  const [stickerConvert, setStickerConvert] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [urlTipo, setUrlTipo] = useState("imagem");
  const [urlCategoria, setUrlCategoria] = useState("Áudios & Mídias");
  const [urlValue, setUrlValue] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const [linkTexto, setLinkTexto] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkCategoria, setLinkCategoria] = useState("Dúvidas Frequentes");
  const [addingLink, setAddingLink] = useState(false);
  const [locNome, setLocNome] = useState("");
  const [locEndereco, setLocEndereco] = useState("");
  const [locLat, setLocLat] = useState("");
  const [locLng, setLocLng] = useState("");
  const [locCategoria, setLocCategoria] = useState("Dúvidas Frequentes");
  const [addingLoc, setAddingLoc] = useState(false);
  const [pollPergunta, setPollPergunta] = useState("");
  const [pollOpcoes, setPollOpcoes] = useState(["", ""]);
  const [pollDias, setPollDias] = useState([]);
  const [pollHoraIni, setPollHoraIni] = useState("");
  const [pollHoraFim, setPollHoraFim] = useState("");
  const [addingPoll, setAddingPoll] = useState(false);

  const addLinkFrase = async () => {
    if (linkTexto.trim().length < 2 || linkUrl.trim().length < 10) {
      toast.error("Informe o texto da mensagem e a URL do link");
      return;
    }
    setAddingLink(true);
    try {
      await api.post("/frases/link", { categoria: linkCategoria, texto: linkTexto.trim(), url: linkUrl.trim() });
      toast.success("Link cadastrado no banco global — chips vão disparar com preview");
      setLinkTexto("");
      setLinkUrl("");
      fetchFrases();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingLink(false);
    }
  };

  const addLocationFrase = async () => {
    const lat = parseFloat(locLat);
    const lng = parseFloat(locLng);
    if (locNome.trim().length < 2 || Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error("Informe o nome do local e coordenadas válidas (lat/lng)");
      return;
    }
    setAddingLoc(true);
    try {
      await api.post("/frases/localizacao", { categoria: locCategoria, nome: locNome.trim(), endereco: locEndereco.trim(), lat, lng });
      toast.success("Localização cadastrada no banco global");
      setLocNome("");
      setLocEndereco("");
      setLocLat("");
      setLocLng("");
      fetchFrases();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingLoc(false);
    }
  };

  const addPollEnquete = async () => {
    const opcoes = pollOpcoes.map((o) => o.trim()).filter(Boolean);
    if (pollPergunta.trim().length < 2 || opcoes.length < 2) {
      toast.error("Informe a pergunta e pelo menos 2 opções");
      return;
    }
    if ((pollHoraIni && !pollHoraFim) || (!pollHoraIni && pollHoraFim)) {
      toast.error("Informe início e fim do horário, ou deixe ambos vazios");
      return;
    }
    setAddingPoll(true);
    try {
      const payload = { categoria: "Enquetes", texto: pollPergunta.trim(), opcoes };
      if (pollDias.length > 0) payload.dias = pollDias;
      if (pollHoraIni && pollHoraFim) {
        payload.hora_inicio = pollHoraIni;
        payload.hora_fim = pollHoraFim;
      }
      await api.post("/frases", payload);
      toast.success("Enquete cadastrada — entra no rodízio dos grupos automaticamente");
      setPollPergunta("");
      setPollOpcoes(["", ""]);
      setPollDias([]);
      setPollHoraIni("");
      setPollHoraFim("");
      fetchFrases();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingPoll(false);
    }
  };

  const fetchFrases = useCallback(async () => {
    try {
      const { data } = await api.get("/frases");
      setFrases(data);
    } catch (e) {
      console.error("Falha ao carregar o banco de frases", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFrases();
  }, [fetchFrases]);

  const filtered = useMemo(() => {
    return frases.filter((f) => {
      const matchCat = categoria === "todas" || f.categoria === categoria;
      const matchSearch = !search || f.texto.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [frases, search, categoria]);

  const addFrase = async (e) => {
    e.preventDefault();
    if (novoTexto.trim().length < 2) return;
    setAdding(true);
    try {
      await api.post("/frases", { categoria: novaCategoria, texto: novoTexto.trim() });
      setNovoTexto("");
      toast.success("Frase adicionada ao banco");
      fetchFrases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("categoria", mediaCategoria);
    fd.append("file", file);
    if (stickerConvert) fd.append("converter_figurinha", "true");
    setUploading(true);
    try {
      await api.post("/frases/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Mídia adicionada ao banco de frases — já entra no loop de disparo");
      fetchFrases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addUrlMedia = async () => {
    if (!urlValue.trim().startsWith("http")) {
      toast.error("Informe uma URL válida (https://...)");
      return;
    }
    setAddingUrl(true);
    try {
      await api.post("/frases/midia-url", { categoria: urlCategoria, tipo: urlTipo, url: urlValue.trim() });
      setUrlValue("");
      toast.success("Mídia por URL adicionada ao banco global");
      fetchFrases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddingUrl(false);
    }
  };

  const saveEdit = async (frase) => {
    try {
      await api.put(`/frases/${frase.id}`, { categoria: frase.categoria, texto: editTexto.trim() });
      setEditingId(null);
      toast.success("Frase atualizada");
      fetchFrases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeFrase = async (frase) => {
    try {
      await api.delete(`/frases/${frase.id}`);
      toast.success("Frase removida");
      fetchFrases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restaurar = async () => {
    if (!window.confirm("Restaurar as 100 frases padrão? Suas frases personalizadas serão substituídas.")) return;
    try {
      await api.post("/frases/restaurar");
      toast.success("Banco de frases restaurado com as 100 frases padrão");
      fetchFrases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="frases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Banco Global de Frases</h1>
          <p className="text-sm text-slate-400 mt-1">
            {frases.length} itens globais (textos, imagens, vídeos, áudios, figurinhas e enquetes) usados pelo motor de maturação de todos os clientes. Gerenciamento exclusivo do administrador.
          </p>
        </div>
        <Button onClick={restaurar} variant="outline" data-testid="btn-restore-phrases"
          className="border-slate-700 text-slate-300 hover:bg-slate-800">
          <RotateCcw className="w-4 h-4 mr-2" /> Restaurar 100 padrão
        </Button>
      </div>

      <form onSubmit={addFrase} className="bg-[#161F32] border border-slate-800 rounded-xl p-5 space-y-3">
        <Textarea
          value={novoTexto}
          onChange={(e) => setNovoTexto(e.target.value)}
          placeholder="Digite uma nova frase, ex: 'Boa tarde! Já viu a promoção dessa semana?'"
          data-testid="phrase-new-text-input"
          className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500 min-h-[70px]"
        />
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={novaCategoria} onValueChange={setNovaCategoria}>
            <SelectTrigger data-testid="phrase-new-category-select" className="bg-slate-800/60 border-slate-700 text-slate-100 sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit" disabled={adding} data-testid="btn-add-phrase"
            className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" /> Adicionar frase</>)}
          </Button>
        </div>
      </form>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-200">Enviar mídia para o banco</p>
          <p className="text-xs text-slate-500">Imagens (JPEG/PNG), vídeos (MP4), áudios (MP3/OGG) e figurinhas (WEBP) entram no loop de disparo automaticamente.</p>
        </div>
        <Select value={mediaCategoria} onValueChange={setMediaCategoria}>
          <SelectTrigger data-testid="media-category-select" className="bg-slate-800/60 border-slate-700 text-slate-100 sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.mp4,.webp,.mp3,.ogg" className="hidden" onChange={uploadMedia} data-testid="media-file-input" />
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none" data-testid="sticker-convert-label">
          <input type="checkbox" checked={stickerConvert} onChange={(e) => setStickerConvert(e.target.checked)}
            data-testid="sticker-convert-checkbox"
            className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-emerald-500" />
          Converter para figurinha
        </label>
        <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-upload-media"
          className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 font-semibold">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Upload className="w-4 h-4 mr-2" /> Enviar mídia</>)}
        </Button>
      </div>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3">
        <div className="min-w-0 lg:w-full xl:w-auto xl:flex-1">
          <p className="text-sm font-semibold text-slate-200">Registrar mídia por URL</p>
          <p className="text-xs text-slate-500">Cole o link direto de uma imagem, vídeo (MP4), áudio ou figurinha (.webp).</p>
        </div>
        <Select value={urlTipo} onValueChange={setUrlTipo}>
          <SelectTrigger data-testid="url-media-tipo-select" className="bg-slate-800/60 border-slate-700 text-slate-100 w-full sm:w-36 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            {MEDIA_TIPOS.map((t) => (
              <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={urlCategoria} onValueChange={setUrlCategoria}>
          <SelectTrigger data-testid="url-media-category-select" className="bg-slate-800/60 border-slate-700 text-slate-100 w-full sm:w-44 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={urlValue} onChange={(e) => setUrlValue(e.target.value)} placeholder="https://exemplo.com/midia.webp"
          data-testid="url-media-input"
          className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 flex-1 min-w-[240px] font-mono text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
        <Button type="button" onClick={addUrlMedia} disabled={addingUrl} data-testid="btn-add-url-media"
          className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold shrink-0 w-full sm:w-auto">
          {addingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" /> Adicionar URL</>)}
        </Button>
      </div>

      <div className="glass-card p-5 sm:p-6 grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="link-location-card">
        <div className="space-y-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-200">Cadastrar Link (disparo com preview)</p>
            <p className="text-xs text-slate-500">O chip envia o texto seguido do link — o WhatsApp gera a prévia clicável.</p>
          </div>
          <Textarea value={linkTexto} onChange={(e) => setLinkTexto(e.target.value)} placeholder="Texto da mensagem que acompanha o link..."
            rows={2} data-testid="link-frase-texto"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
          <div className="flex flex-col sm:flex-row gap-3">
            <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://seusite.com/oferta"
              data-testid="link-frase-url"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 flex-1 font-mono text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
            <Select value={linkCategoria} onValueChange={setLinkCategoria}>
              <SelectTrigger data-testid="link-frase-categoria" className="bg-slate-800/60 border-slate-700 text-slate-100 sm:w-48 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" onClick={addLinkFrase} disabled={addingLink} data-testid="btn-add-link-frase"
            className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            {addingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" /> Cadastrar link</>)}
          </Button>
        </div>
        <div className="space-y-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-200">Cadastrar Localização</p>
            <p className="text-xs text-slate-500">O chip envia um pin de localização real do WhatsApp (nome + coordenadas).</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={locNome} onChange={(e) => setLocNome(e.target.value)} placeholder="Nome do local (ex: Loja Centro)"
              data-testid="loc-frase-nome"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
            <Input value={locEndereco} onChange={(e) => setLocEndereco(e.target.value)} placeholder="Endereço (opcional)"
              data-testid="loc-frase-endereco"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
            <Input value={locLat} onChange={(e) => setLocLat(e.target.value)} placeholder="Latitude (ex: -24.0463)"
              data-testid="loc-frase-lat"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 font-mono text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
            <Input value={locLng} onChange={(e) => setLocLng(e.target.value)} placeholder="Longitude (ex: -52.3780)"
              data-testid="loc-frase-lng"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 font-mono text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={locCategoria} onValueChange={setLocCategoria}>
              <SelectTrigger data-testid="loc-frase-categoria" className="bg-slate-800/60 border-slate-700 text-slate-100 sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" onClick={addLocationFrase} disabled={addingLoc} data-testid="btn-add-loc-frase"
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              {addingLoc ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" /> Cadastrar localização</>)}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 space-y-3" data-testid="poll-card">
        <div>
          <p className="text-sm font-semibold text-slate-200">Cadastrar Enquete (grupos)</p>
          <p className="text-xs text-slate-500">Um chip envia a pergunta no grupo e os outros votam sozinhos. Opcional: limite por dias e horário.</p>
        </div>
        <Input value={pollPergunta} onChange={(e) => setPollPergunta(e.target.value)} placeholder="Pergunta (ex: Almoço amanhã?)"
          data-testid="poll-pergunta-input"
          className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pollOpcoes.map((op, i) => (
            <div key={i} className="flex gap-2">
              <Input value={op} onChange={(e) => setPollOpcoes((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
                placeholder={`Opção ${i + 1}`} data-testid={`poll-opcao-input-${i}`}
                className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500" />
              {pollOpcoes.length > 2 && (
                <Button type="button" variant="outline" onClick={() => setPollOpcoes((prev) => prev.filter((_, j) => j !== i))}
                  className="border-slate-700 text-slate-300 shrink-0" data-testid={`poll-opcao-remove-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {pollOpcoes.length < 12 && (
          <Button type="button" variant="outline" onClick={() => setPollOpcoes((prev) => [...prev, ""])}
            className="border-slate-700 text-slate-300" data-testid="poll-opcao-add">
            <Plus className="w-4 h-4 mr-2" /> Adicionar opção
          </Button>
        )}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex gap-1.5 flex-wrap" data-testid="poll-dias">
            {DIAS_SEMANA.map(([label, d]) => (
              <button key={d} type="button" onClick={() => setPollDias((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]))}
                data-testid={`poll-dia-${d}`}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${pollDias.includes(d) ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <Input type="time" value={pollHoraIni} onChange={(e) => setPollHoraIni(e.target.value)}
              data-testid="poll-hora-ini" className="bg-slate-800/60 border-slate-700 text-slate-100 font-mono h-9 w-32" />
            <span className="text-slate-500 text-xs">até</span>
            <Input type="time" value={pollHoraFim} onChange={(e) => setPollHoraFim(e.target.value)}
              data-testid="poll-hora-fim" className="bg-slate-800/60 border-slate-700 text-slate-100 font-mono h-9 w-32" />
          </div>
        </div>
        <p className="text-[11px] text-slate-500">Sem dia/hora = vale qualquer dia, dentro da agenda global (padrão 08:00–22:30).</p>
        <Button type="button" onClick={addPollEnquete} disabled={addingPoll} data-testid="btn-add-poll"
          className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
          {addingPoll ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" /> Cadastrar enquete</>)}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar frase..."
            data-testid="phrase-search-input"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 pl-9 focus-visible:ring-2 focus-visible:ring-emerald-500"
          />
        </div>
        <Select value={categoria} onValueChange={setCategoria}>
          <SelectTrigger data-testid="phrase-category-filter" className="bg-slate-800/60 border-slate-700 text-slate-100 sm:w-64">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="todas">Todas as categorias</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl divide-y divide-slate-800/80 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="phrases-empty-state">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-600" />
            Nenhuma frase encontrada.
          </div>
        ) : (
          filtered.map((f) => (
            <div key={f.id} className="p-4 flex items-start gap-3 hover:bg-slate-800/30 transition-colors" data-testid={`phrase-table-row-${f.id}`}>
              <div className="flex-1 min-w-0">
                {editingId === f.id ? (
                  <div className="space-y-2">
                    <Textarea value={editTexto} onChange={(e) => setEditTexto(e.target.value)}
                      data-testid={`phrase-edit-input-${f.id}`}
                      className="bg-slate-800/60 border-slate-700 text-slate-100 min-h-[60px] focus-visible:ring-2 focus-visible:ring-emerald-500" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(f)} data-testid={`phrase-edit-save-${f.id}`}
                        className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 h-8 text-xs font-semibold">
                        <Check className="w-3.5 h-3.5 mr-1" /> Salvar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}
                        className="border-slate-700 text-slate-300 h-8 text-xs">
                        <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {f.tipo && f.tipo !== "texto" && f.media_path && (
                      <div className="mb-2">
                        {f.tipo === "video" ? (
                          <video src={mediaSrc(f.media_path)} controls className="max-h-44 rounded-lg border border-slate-700 bg-black" data-testid={`phrase-media-video-${f.id}`} />
                        ) : f.tipo === "audio" ? (
                          <audio src={mediaSrc(f.media_path)} controls className="w-full max-w-sm" data-testid={`phrase-media-audio-${f.id}`} />
                        ) : (
                          <img src={mediaSrc(f.media_path)} alt={f.original_filename || "mídia"} className={`rounded-lg border border-slate-700 bg-slate-900 object-cover ${f.tipo === "figurinha" ? "w-20 h-20" : "max-h-44"}`} data-testid={`phrase-media-img-${f.id}`} />
                        )}
                      </div>
                    )}
                    <p className="text-sm text-slate-200 leading-relaxed">{f.texto}</p>
                    {f.tipo === "enquete" && f.opcoes && (
                      <div className="mt-1.5 text-xs text-slate-400" data-testid={`phrase-poll-opts-${f.id}`}>
                        <span className="text-slate-500">Opções: </span>{f.opcoes.join(" · ")}
                        {(f.dias?.length > 0 || f.hora_inicio) && (
                          <span className="text-slate-500">
                            {" "}· {f.dias?.length > 0 ? `dias ${f.dias.map((d) => DIAS_SEMANA[d][0]).join(",")}` : "todo dia"}
                            {f.hora_inicio ? ` ${f.hora_inicio}–${f.hora_fim}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider bg-slate-800 text-slate-400 border border-slate-700">
                        {f.categoria}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                        {f.tipo === "video" ? <Film className="w-3 h-3" /> : f.tipo === "imagem" ? <ImageIcon className="w-3 h-3" /> : f.tipo === "figurinha" ? <Sticker className="w-3 h-3" /> : f.tipo === "audio" ? <Mic className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                        {TIPO_LABEL[f.tipo || "texto"]}
                      </span>
                    </div>
                  </>
                )}
              </div>
              {editingId !== f.id && (
                <div className="flex gap-1 shrink-0">
                  {(!f.tipo || f.tipo === "texto") && (
                  <button onClick={() => { setEditingId(f.id); setEditTexto(f.texto); }}
                    data-testid={`phrase-edit-button-${f.id}`}
                    className="p-2 text-slate-500 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  )}
                  <button onClick={() => removeFrase(f)}
                    data-testid={`phrase-delete-button-${f.id}`}
                    className="p-2 text-slate-500 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
